import classNames from 'classnames';
import React, {ReactElement, useCallback} from 'react';

import {Spinner} from '../../../../resources/Icons';
import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {
  MarketplaceCropType,
  MarketplaceServiceType,
  ProductSearchParams,
  useFindProductsQuery,
} from '../../../query/graphql';
import {SortOrder, SupplierBasic} from '../../marketplace/types';
import {productSearchParamsToQuery} from '../../marketplace/util';
import Pagination from '../Pagination';
import SearchHeader from './SearchHeader';
import SupplierProducts from './SupplierProducts';

type Props = {
  params: ProductSearchParams;
  onSortOrderChange: (orderKey: keyof typeof SortOrder) => void;
  onTagClick: (key: keyof ProductSearchParams | (keyof ProductSearchParams)[]) => void;
  approvedSuppliers: SupplierBasic[];
  allServices: MarketplaceServiceType[];
  allCrops: MarketplaceCropType[];
};

const ProductSearch = ({
  params,
  onSortOrderChange,
  onTagClick,
  approvedSuppliers,
  allServices,
  allCrops,
}: Props): ReactElement => {
  const {data, isFetching: loading, error} = useFindProductsQuery({searchParams: params});

  const products = data?.productSearch.products;
  const hasProducts = !!products && products.length > 0;

  const getPageUrl = useCallback(
    (page: number) => {
      return {query: productSearchParamsToQuery({...params, page: page > 1 ? page : undefined})};
    },
    [params]
  );

  return (
    <>
      <SearchHeader
        params={params}
        total={data?.productSearch?.total}
        loading={hasProducts && loading}
        onSortOrderChange={onSortOrderChange}
        onTagClick={onTagClick}
        approvedSuppliers={approvedSuppliers}
        allServices={allServices}
        allCrops={allCrops}
      />
      <Col className={'gap-md items-stretch'}>
        {!!error && <span className={'pt-xl text-center text-red-200'}>{String(error) || 'An error occurred.'}</span>}
        {!error && !hasProducts && loading && (
          <Row className={'w-full pt-xl justify-center'}>
            <Spinner />
          </Row>
        )}
        {!error && !hasProducts && !loading && (
          <span className={'pt-xl text-center'}>No products found matching your filters.</span>
        )}
        {!error && hasProducts && (
          <>
            <SupplierProducts
              products={products}
              approvedSuppliers={approvedSuppliers}
              className={classNames(loading && 'opacity-20')}
            />
            {data.productSearch.pageCount > 1 && (
              <Pagination
                className={'mt-lg'}
                numPageLinks={4}
                page={data.productSearch.page}
                pageCount={data.productSearch.pageCount}
                getPageUrl={getPageUrl}
              />
            )}
          </>
        )}
      </Col>
    </>
  );
};

export default ProductSearch;
